import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Box, LogOut, Package, Shield, User as UserIcon } from "lucide-react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <header
      data-testid="main-navbar"
      className="sticky top-0 z-50 backdrop-blur-xl bg-white/60 border-b border-white/40 shadow-sm"
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-2 group">
          <div className="w-9 h-9 rounded-lg bg-[#0F4E2A] grid place-items-center text-[#B5DC57]">
            <Box className="w-5 h-5" />
          </div>
          <div className="leading-tight">
            <div className="font-latin font-extrabold text-lg tracking-tight">MyanTax <span className="text-[#7CC576]">Box</span></div>
            <div className="text-[10px] tracking-wide text-zinc-500">အခွန်တွက်စက် App</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <NavLink to="/" data-testid="nav-home" className={({isActive}) => `text-sm font-medium hover:text-[#7CC576] transition-colors ${isActive ? "text-[#7CC576]" : "text-zinc-700"}`}>ပင်မ</NavLink>
          {user && (
            <NavLink to="/dashboard" data-testid="nav-dashboard" className={({isActive}) => `text-sm font-medium hover:text-[#7CC576] transition-colors ${isActive ? "text-[#7CC576]" : "text-zinc-700"}`}>ဝယ်ယူမှုများ</NavLink>
          )}
          {user?.role === "admin" && (
            <NavLink to="/admin" data-testid="nav-admin" className={({isActive}) => `text-sm font-medium hover:text-[#7CC576] transition-colors ${isActive ? "text-[#7CC576]" : "text-zinc-700"}`}>စီမံခန့်ခွဲ</NavLink>
          )}
        </nav>

        <div className="flex items-center gap-3">
          {!user ? (
            <>
              <Button data-testid="login-btn" variant="ghost" onClick={() => navigate("/login")}>ဝင်ရောက်ရန်</Button>
              <Button data-testid="register-btn" className="bg-[#7CC576] hover:bg-[#5FB259] rounded-lg" onClick={() => navigate("/register")}>
                စတင်ရန်
              </Button>
            </>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-zinc-100">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-zinc-900 text-white grid place-items-center text-xs font-semibold">
                      {user.name?.[0]?.toUpperCase() || "U"}
                    </div>
                  )}
                  <span className="hidden sm:block text-sm font-medium">{user.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-purchases" onClick={() => navigate("/dashboard")}>
                  <Package className="w-4 h-4 mr-2" /> ဝယ်ယူမှုများ
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}>
                    <Shield className="w-4 h-4 mr-2" /> စီမံခန့်ခွဲ Panel
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); navigate("/"); }}>
                  <LogOut className="w-4 h-4 mr-2" /> ထွက်ရန်
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </header>
  );
}
